import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import { baseUrl } from "../../baseUrl";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../api";

const GenerateNeetFlexi = () => {

  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [topicsMap, setTopicsMap] = useState({});
  const [searchMap, setSearchMap] = useState({});

  const subjects = [
    { key: "physics", name: "Physics", total: 45 },
    { key: "chemistry", name: "Chemistry", total: 45 },
    { key: "biology", name: "Biology", total: 90 },
  ];

  const [data, setData] = useState({
    physics: { easy: 15, medium: 15, useAllTopics: false, topics: [] },
    chemistry: { easy: 15, medium: 15, useAllTopics: false, topics: [] },
    biology: { easy: 30, medium: 30, useAllTopics: false, topics: [] },
  });

  const handleChange = (subject, level, value) => {
    setData((prev) => {
      const subjectData = { ...prev[subject] };
      const total = subjects.find(s => s.key === subject).total;

      subjectData[level] = value;

      if (subjectData.easy + subjectData.medium > total) {
        if (level === "easy") {
          subjectData.medium = total - value;
        } else {
          subjectData.easy = total - value;
        }
      }

      return {
        ...prev,
        [subject]: subjectData,
      };
    });
  };

  const getHard = (subjectKey) => {
    const subject = subjects.find(s => s.key === subjectKey);
    const { easy, medium } = data[subjectKey];
    return subject.total - easy - medium;
  };

  const handleGenerateExam = async () => {
    if (!data.physics.useAllTopics && data.physics.topics.length === 0) {
      return Swal.fire({
        icon: "warning",
        title: "Missing Topics",
        text: "Select at least 1 Physics topic",
      });

    }
    if (!data.chemistry.useAllTopics && data.chemistry.topics.length === 0) {
      return Swal.fire({
        icon: "warning",
        title: "Missing Topics",
        text: "Select at least 1 Chemistry topic",
      });

    }
    if (!data.biology.useAllTopics && data.biology.topics.length === 0) {
      return Swal.fire({
        icon: "warning",
        title: "Missing Topics",
        text: "Select at least 1 Biology topic",
      });

    }

    try {
      setLoading(true);

      const payload = {
        studentId: user.id,

        physics: {
          easy: data.physics.easy,
          medium: data.physics.medium,
          hard: getHard("physics"),
          topics: data.physics.useAllTopics ? [] : data.physics.topics,
        },

        chemistry: {
          easy: data.chemistry.easy,
          medium: data.chemistry.medium,
          hard: getHard("chemistry"),
          topics: data.chemistry.useAllTopics ? [] : data.chemistry.topics,
        },

        biology: {
          easy: data.biology.easy,
          medium: data.biology.medium,
          hard: getHard("biology"),
          topics: data.biology.useAllTopics ? [] : data.biology.topics,
        },

        timeLimit: 180,
      };


      const res = await api.post(
        `${baseUrl}/dynamic-exam/generate-flexi`,
        payload
      );

      navigate(`/dynamic-exam/${res.data.examId}`);

    } catch (err) {
      console.log(err);
      const errorData = err?.response?.data;

      if (errorData?.available && errorData?.required) {
        const { message, required, available, topics } = errorData;

        // shortage calculation
        const shortage = {
          easy: Math.max(0, required.easy - available.easy),
          medium: Math.max(0, required.medium - available.medium),
          hard: Math.max(0, required.hard - available.hard),
        };

        // extract subject name (Physics/Chemistry/Biology)
        const subjectName = message.split(" ")[4];

        // map topic IDs → names
        const subjectTopics = topicsMap[subjectName] || [];

        const topicNames = topics
          .map(id => subjectTopics.find(t => t._id === id)?.topicName)
          .filter(Boolean);

        Swal.fire({
          icon: "error",
          title: "Insufficient Questions",
          width: 650,
          confirmButtonText: "Adjust Selection",
          customClass: {
            popup: "rounded-4"
          },
          html: `
    <div style="text-align:left; font-family: Inter, sans-serif;">

      <!-- Title -->
      <div style="font-size:15px; color:#555; margin-bottom:12px;">
        ${message}
      </div>

      <!-- Table -->
      <table style="width:100%; border-collapse:collapse; margin-bottom:15px;">
        <thead>
          <tr style="background:#f8f9fa;">
            <th style="padding:8px; text-align:left;">Difficulty</th>
            <th style="padding:8px; text-align:center;">Required</th>
            <th style="padding:8px; text-align:center;">Available</th>
            <th style="padding:8px; text-align:center;">Shortage</th>
          </tr>
        </thead>

        <tbody>

          <tr>
            <td style="padding:8px;">Easy</td>
            <td style="text-align:center;">${required.easy}</td>
            <td style="text-align:center;">${available.easy}</td>
            <td style="text-align:center; color:${shortage.easy ? "#dc3545" : "#28a745"};">
              ${shortage.easy ? shortage.easy : "OK"}
            </td>
          </tr>

          <tr style="background:#fafafa;">
            <td style="padding:8px;">Medium</td>
            <td style="text-align:center;">${required.medium}</td>
            <td style="text-align:center;">${available.medium}</td>
            <td style="text-align:center; color:${shortage.medium ? "#dc3545" : "#28a745"};">
              ${shortage.medium ? shortage.medium : "OK"}
            </td>
          </tr>

          <tr>
            <td style="padding:8px;">Hard</td>
            <td style="text-align:center;">${required.hard}</td>
            <td style="text-align:center;">${available.hard}</td>
            <td style="text-align:center; color:${shortage.hard ? "#dc3545" : "#28a745"};">
              ${shortage.hard ? shortage.hard : "OK"}
            </td>
          </tr>

        </tbody>
      </table>

      <!-- Topics Section -->
      <div style="margin-bottom:10px; font-weight:600;">
        Selected Topics
      </div>

      <div style="
        max-height:140px;
        overflow-y:auto;
        border:1px solid #e9ecef;
        border-radius:8px;
        padding:8px;
        background:#fff;
      ">
        ${topicNames.length > 0
              ? topicNames.map(t => `
            <div style="
              padding:6px 8px;
              border-bottom:1px solid #f1f1f1;
              font-size:14px;
            ">
              ${t}
            </div>
          `).join("")
              : `<div style="color:#888;">No topics selected</div>`
            }
      </div>

      <!-- Footer Hint -->
      <div style="
        margin-top:12px;
        font-size:13px;
        color:#6c757d;
      ">
        Increase topic coverage or reduce difficulty distribution to proceed.
      </div>

    </div>
  `,
        });



      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorData?.message || "Something went wrong",
        });
      }
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await axios.get(`${baseUrl}/topic/all`);

        const map = {};
        res.data.forEach(t => {
          if (!map[t.subject]) map[t.subject] = [];
          map[t.subject].push(t);
        });

        setTopicsMap(map);
      } catch (err) {
        console.log(err);
      }
    };

    fetchTopics();
  }, []);


  return (
    <div className="container mt-4">
      <div className="row justify-content-center">

        <div className="col-xl-6 col-lg-8 col-md-10">

          <div className="card shadow-lg border-0 rounded-4 p-4">

            <h4 className="text-center mb-4">NEET Flexi Test</h4>

            {subjects.map((subject) => (
              <div key={subject.key} className="mb-4 p-3 border rounded-3">

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold text-primary">{subject.name}</h5>

                  <span className="badge bg-secondary">
                    Total: {subject.total}
                  </span>
                </div>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <span className="fw-semibold text-muted">
                    {data[subject.key].useAllTopics ? "All Topics Selected" : "Custom Topics"}
                  </span>

                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={data[subject.key].useAllTopics}
                      onChange={() =>
                        setData(prev => ({
                          ...prev,
                          [subject.key]: {
                            ...prev[subject.key],
                            useAllTopics: !prev[subject.key].useAllTopics,
                            topics: []
                          }
                        }))
                      }
                    />
                  </div>
                  <hr className="my-2" />
                </div>

                {!data[subject.key].useAllTopics && (
                  <div className="d-flex gap-2 mb-2">

                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search topics..."
                      onChange={(e) => {
                        setSearchMap(prev => ({
                          ...prev,
                          [subject.key]: e.target.value
                        }));
                      }}
                    />

                    <button
                      className="btn btn-outline-primary"
                      onClick={() => {
                        const allIds = topicsMap[subject.name]?.map(t => t._id) || [];

                        setData(prev => ({
                          ...prev,
                          [subject.key]: {
                            ...prev[subject.key],
                            topics: allIds
                          }
                        }));
                      }}
                    >
                      All
                    </button>

                    <button
                      className="btn btn-outline-danger"
                      onClick={() => {
                        setData(prev => ({
                          ...prev,
                          [subject.key]: {
                            ...prev[subject.key],
                            topics: []
                          }
                        }));
                      }}
                    >
                      Clear
                    </button>

                  </div>
                )}

                {!data[subject.key].useAllTopics && (
                  <div className="d-flex gap-3 align-items-center mb-2">
                    <label className="form-label fw-semibold">Select Topics</label>
                    <div className="mb-2">
                      <span className="badge bg-primary">
                        Selected: {data[subject.key].topics.length}
                      </span>
                    </div>
                  </div>
                )}




                {!data[subject.key].useAllTopics && (
                  <div className="mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>


                    {(topicsMap[subject.name] || [])
                      .filter(t =>
                        t.topicName
                          .toLowerCase()
                          .includes((searchMap[subject.key] || "").toLowerCase())
                      )
                      .map((topic,idx) => (

                        <div
                          key={topic._id}
                          className={`p-2 mb-1 rounded d-flex align-items-center justify-content-between 
    ${data[subject.key].topics.includes(topic._id) ? "bg-primary text-white" : "bg-light"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const exists = data[subject.key].topics.includes(topic._id);

                            const updated = exists
                              ? data[subject.key].topics.filter(id => id !== topic._id)
                              : [...data[subject.key].topics, topic._id];

                            setData(prev => ({
                              ...prev,
                              [subject.key]: {
                                ...prev[subject.key],
                                topics: updated
                              }
                            }));
                          }}
                        >
                          <div>
                            <span className="mr-3">{idx + 1}.</span> <span>{topic.topicName}</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={data[subject.key].topics.includes(topic._id)}
                            readOnly
                          />
                        </div>

                      ))}

                  </div>
                )}

                {/* EASY */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Easy</span>
                    <strong>{data[subject.key].easy}</strong>
                  </div>

                  <input
                    type="range"
                    min="0"
                    max={subject.total}
                    value={data[subject.key].easy}
                    onChange={(e) =>
                      handleChange(subject.key, "easy", Number(e.target.value))
                    }
                    className="form-range"
                  />
                </div>

                {/* MEDIUM */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Medium</span>
                    <strong>{data[subject.key].medium}</strong>
                  </div>

                  <input
                    type="range"
                    min="0"
                    max={subject.total}
                    value={data[subject.key].medium}
                    onChange={(e) =>
                      handleChange(subject.key, "medium", Number(e.target.value))
                    }
                    className="form-range"
                  />
                </div>

                {/* HARD */}
                <div className="mt-3 p-2 bg-light rounded text-center">
                  <span className="text-muted">Hard (Auto): </span>
                  <strong>{getHard(subject.key)}</strong>
                </div>

              </div>
            ))}

            <div className="text-center mt-3">
              <button
                className="btn btn-primary px-5 py-2 rounded-pill fw-bold"

                onClick={handleGenerateExam}
                disabled={loading}
              >
                {loading ? "Generating..." : "Start Full Test"}
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default GenerateNeetFlexi;